import {
  type QueryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import {
  byProductDisplayOrder,
  MAGAZINE_GROUP_CODE,
} from '../../../../utils/product-display-order';
import { productShape } from '../../../../utils/api/types/access-api-types';

export const subscriptionsWithStatusShape = z.array(
  z.object({
    enabled: z.boolean().optional(),
    subscriptionId: z.string(),
    entitlementId: z.string().optional(),
    orderId: z.string().optional(),
    endDate: z.string().datetime(),
    entitlementType: z.string().optional(),
    product: productShape,
  })
);

export type SubscriptionWithStatus = z.infer<
  typeof subscriptionsWithStatusShape
>[0];

export interface GetSubscriptionsWithStatusParams {
  orgId?: string;
  userId?: string;
  enabled?: boolean;
}

export async function getSubscriptionsWithStatus({
  orgId,
  userId,
  enabled,
}: GetSubscriptionsWithStatusParams): Promise<SubscriptionWithStatus[]> {
  const subscriptionsWithStatus = await fetcher(
    `/composite/subscriptions/orgs/${orgId}/users/${userId}`,
    {
      service: '/fulfillment',
      version: '/v2',
      queryParameters:
        enabled !== undefined ? { enabled: enabled.toString() } : undefined,
    }
  );

  return subscriptionsWithStatusShape.parse(subscriptionsWithStatus);
}

export const getSubscriptionsWithStatusApiQueryKey = (
  args?: GetSubscriptionsWithStatusParams
) => {
  const { orgId, userId, enabled } = args ?? {};
  return ['/v2', '/composite/subscriptions', orgId, userId, enabled].filter(
    Boolean
  );
};

const bySubscriptionDisplayOrder =
  byProductDisplayOrder<SubscriptionWithStatus>({
    name: (subscription) => subscription.product.productName,
    isMagazine: (subscription) =>
      subscription.product.groupCode === MAGAZINE_GROUP_CODE,
  });

export function useGetSubscriptionsWithStatusApi(
  params: GetSubscriptionsWithStatusParams,
  customQueryOptions?: QueryOptions<SubscriptionWithStatus[], ApiError>
): UseQueryResult<SubscriptionWithStatus[], ApiError> {
  return useQuery({
    queryKey: getSubscriptionsWithStatusApiQueryKey(params),
    queryFn: () => getSubscriptionsWithStatus(params),
    enabled: !!params.orgId && !!params.userId,
    select: (subscriptions: SubscriptionWithStatus[]) =>
      [...subscriptions].sort(bySubscriptionDisplayOrder),
    ...customQueryOptions,
  });
}
